import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Shield, Loader2, Save, Users, Crown, CreditCard } from "lucide-react";

export function AdminDialog({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proPrice, setProPrice] = useState("");
  const [freeLimit, setFreeLimit] = useState("");
  const [emails, setEmails] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setProPrice(String(data.pro_price));
        setFreeLimit(String(data.free_daily_limit));
        setEmails((data.admin_emails || []).join("\n"));
        try { const s = await api.get("/admin/stats"); setStats(s.data); } catch { /* ignore */ }
      } catch {
        toast.error("Gagal memuat pengaturan admin");
      } finally { setLoading(false); }
    })();
  }, [open]);

  const save = async () => {
    const price = parseInt(proPrice, 10);
    const limit = parseInt(freeLimit, 10);
    const adminEmails = emails.split(/[\n,]/).map((e) => e.trim()).filter(Boolean);
    if (!price || price < 1000) return toast.error("Harga Pro minimal Rp1.000");
    if (!limit || limit < 1) return toast.error("Limit harian minimal 1");
    if (!adminEmails.length) return toast.error("Minimal 1 email admin");
    setSaving(true);
    try {
      await api.put("/admin/settings", { pro_price: price, free_daily_limit: limit, admin_emails: adminEmails });
      toast.success("Pengaturan tersimpan");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="rounded-lg border border-slate-200 p-3 text-center">
      <Icon size={16} className="mx-auto text-sky-600 mb-1" />
      <div className="text-lg font-extrabold text-slate-900">{value ?? "–"}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Shield size={20} className="text-sky-600" /> Panel Admin
          </DialogTitle>
          <DialogDescription>Atur harga paket Pro, limit harian pengguna gratis, dan daftar admin.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-sky-600" /></div>
        ) : (
          <div className="space-y-4">
            {stats && (
              <div className="grid grid-cols-4 gap-2">
                <Stat icon={Users} label="Pengguna" value={stats.total_users} />
                <Stat icon={Crown} label="Pro" value={stats.pro_users} />
                <Stat icon={CreditCard} label="Transaksi" value={stats.paid_payments} />
                <Stat icon={Shield} label="Resi hari ini" value={stats.labels_today} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Harga Pro (Rp / bulan)</Label>
                <Input type="number" data-testid="admin-pro-price-input" value={proPrice} onChange={(e) => setProPrice(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Limit harian (gratis)</Label>
                <Input type="number" data-testid="admin-free-limit-input" value={freeLimit} onChange={(e) => setFreeLimit(e.target.value)} />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Email Admin (satu per baris / pisah koma)</Label>
              <Textarea data-testid="admin-emails-input" value={emails} onChange={(e) => setEmails(e.target.value)}
                className="min-h-[80px] font-mono text-sm" />
            </div>

            <Button data-testid="btn-save-admin-settings" onClick={save} disabled={saving}
              className="w-full bg-sky-600 hover:bg-sky-700 gap-2">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Pengaturan
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
